import {setScores} from "@/redux/slices/commonSlices/viewTabsSlice";

import {useCallback, useEffect, useMemo, useState} from "react";

import {useViewTabs} from "@hooks/ViewTabs";
import bar_icon from "@images/table/bar-icon.svg";
import {
  getSessionsByCountry,
  setMapChartsDate,
} from "@redux/slices/dashboardSlices/dashboardCallSlice";
import {
  clearOptions,
  getCountries,
} from "@redux/slices/productSlices/optionsSlice";
import {SwitchButton} from "@ui/buttons/SwitchButton";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {Spinner} from "@ui/general/Spinner";
import {WarningData} from "@ui/general/WarningData";
import {RootIcon} from "@ui/icons/RootIcon";
import {H3} from "@ui/titles/H3";
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
} from "chart.js";
import moment from "moment";
import {Bar} from "react-chartjs-2";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";

import {ChartLabel} from "../../components/ChartLabel";
import {Head} from "../../components/Head";
import styles from "./styles.module.scss";

ChartJS.register(
  ArcElement,
  Title,
  Filler,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  LineController,
  BarController,
);

const geoUrl =
  "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-50m.json";

export const MarkerTooltip = (props) => {
  const {t} = useTranslation();
  const {enter, leave, item, x, y} = props;
  const {isMobile} = useSelector((state) => state.common.settings);

  const displayName = item.region || item.country;

  return (
    <div
      onMouseEnter={enter}
      onMouseLeave={leave}
      className={styles.mapMark}
      style={{
        left: isMobile ? "unset" : `${x + 3}px`,
        top: isMobile ? "10px" : `${y + 3}px`,
        right: isMobile ? "10px" : "unset",
      }}
    >
      <table>
        <tbody>
          <tr>
            <td>
              <SmallDesc>
                <span>{t("page.dashboard.region")}</span>
              </SmallDesc>
            </td>
            <td>
              <SmallDesc>
                <strong>{displayName}</strong>
              </SmallDesc>
            </td>
          </tr>
          <tr>
            <td>
              <SmallDesc>
                <span>{t("page.dashboard.simulations")}</span>
              </SmallDesc>
            </td>
            <td>
              <SmallDesc>
                <strong>{item.count}</strong>
              </SmallDesc>
            </td>
          </tr>
          <tr>
            <td>
              <SmallDesc>
                <span>{t("page.dashboard.average_score")}</span>
              </SmallDesc>
            </td>
            <td>
              <SmallDesc>
                <strong>
                  {item.avg_score && Number(item.avg_score).toFixed(1)}
                </strong>
              </SmallDesc>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const coordinatesCache = new Map();

const clearCoordinatesCache = () => {
  coordinatesCache.clear();
};

function matchCountry(searchName, countriesFromApi) {
  if (
    !searchName ||
    !Array.isArray(countriesFromApi) ||
    countriesFromApi.length === 0
  ) {
    return null;
  }

  const cacheKey = `country_${searchName.toLowerCase().trim()}`;
  if (coordinatesCache.has(cacheKey)) {
    return coordinatesCache.get(cacheKey);
  }

  const lower = (s) => (s ? s.toLowerCase().trim() : "");
  const searchNameLower = lower(searchName);

  let country = countriesFromApi.find(
    (c) => c.name && lower(c.name) === searchNameLower,
  );

  if (!country) {
    country = countriesFromApi.find(
      (c) =>
        (c.name && lower(c.name).includes(searchNameLower)) ||
        searchNameLower.includes(lower(c.name)),
    );
  }

  let result = null;
  if (country && country.geo) {
    const {geo} = country;

    if (Array.isArray(geo) && geo.length >= 2) {
      result = {
        lat: geo[1],
        lon: geo[0],
      };
    } else {
      const {latitude, longitude} = geo;

      if (latitude !== undefined && longitude !== undefined) {
        result = {
          lat: latitude,
          lon: longitude,
        };
      }
    }
  }

  coordinatesCache.set(cacheKey, result);
  return result;
}

function matchRegion(searchName, countriesFromApi) {
  if (
    !searchName ||
    !Array.isArray(countriesFromApi) ||
    countriesFromApi.length === 0
  ) {
    return null;
  }

  const cacheKey = `region_${searchName.toLowerCase().trim()}`;
  if (coordinatesCache.has(cacheKey)) {
    return coordinatesCache.get(cacheKey);
  }

  const lower = (s) => (s ? s.toLowerCase().trim() : "");
  const searchNameLower = lower(searchName);
  let result = null;

  for (const country of countriesFromApi) {
    if (!country.subdivisions || !Array.isArray(country.subdivisions)) {
      continue;
    }

    let subdivision = country.subdivisions.find(
      (sub) => sub.name && lower(sub.name) === searchNameLower,
    );

    if (!subdivision) {
      subdivision = country.subdivisions.find(
        (sub) =>
          sub.name &&
          (lower(sub.name).includes(searchNameLower) ||
            searchNameLower.includes(lower(sub.name))),
      );
    }

    if (subdivision?.geo) {
      const {geo} = subdivision;
      if (Array.isArray(geo) && geo.length >= 2) {
        result = {
          lat: geo[1],
          lon: geo[0],
        };
        break;
      }
      const {latitude, longitude} = geo;
      if (latitude !== undefined && longitude !== undefined) {
        result = {
          lat: latitude,
          lon: longitude,
        };
        break;
      }
    }
  }

  coordinatesCache.set(cacheKey, result);
  return result;
}

const renderMarkers = (
  sessionsByCountry,
  sessionScore,
  handleMouseEnterMark,
  handleMouseLeaveMark,
  countriesFromApi,
) => {
  if (!Array.isArray(sessionsByCountry?.raw)) return [];
  if (!Array.isArray(countriesFromApi) || countriesFromApi.length === 0) {
    return [];
  }

  const values = sessionsByCountry.raw
    .map((item) => {
      if (sessionScore === "simulations") {
        return parseFloat(item.count) || 0;
      } else {
        return parseFloat(item.avg_score) || 0;
      }
    })
    .filter((value) => Number.isFinite(value) && value > 0);

  const minValue = values.length > 0 ? Math.min(...values) : 0;
  const maxValue = values.length > 0 ? Math.max(...values) : 1;
  const valueRange = maxValue - minValue;

  const isAllSame = valueRange === 0;

  const markers = sessionsByCountry.raw
    .map((item, idx) => {
      let coords = null;
      const searchName = item.region || item.country;

      if (!searchName) return null;

      if (item.region) {
        const regionObj = matchRegion(searchName, countriesFromApi);
        coords = regionObj ? {lat: regionObj.lat, lon: regionObj.lon} : null;
      } else if (item.country) {
        const countryObj = matchCountry(searchName, countriesFromApi);
        coords = countryObj ? {lat: countryObj.lat, lon: countryObj.lon} : null;
      }

      if (!coords) {
        return null;
      }

      const count = parseFloat(item.count) || 0;
      const avgScore = parseFloat(item.avg_score) || 0;
      const currentValue = sessionScore === "simulations" ? count : avgScore;

      let r;
      if (!Number.isFinite(currentValue) || currentValue <= 0) {
        r = 2;
      } else if (isAllSame) {
        r = 4.5;
      } else {
        const normalizedValue = (currentValue - minValue) / valueRange;
        r = 2 + normalizedValue * 5;
      }

      return (
        <Marker
          key={searchName || idx}
          coordinates={[coords.lon, coords.lat]}
          onMouseEnter={(e) => handleMouseEnterMark(e, item)}
          onMouseLeave={handleMouseLeaveMark}
        >
          <circle
            r={r}
            fill={"#394A97"}
            stroke={"#FFFFFF"}
            strokeWidth={0.5}
          />
        </Marker>
      );
    })
    .filter(Boolean);

  return markers.length > 0 ? markers : [];
};

export const PerformanceCountry = () => {
  const minDate = moment().subtract(10, "years").toDate();
  const [withArchived, setWithArchived] = useState(false);
  const {width, isMobile, language} = useSelector(
    (state) => state.common.settings,
  );
  const {scores} = useSelector((state) => state.common.viewTabs);
  const {data, status, error, date} = useSelector(
    (state) => state.dashboard.call.map,
  );
  const {countries: countriesFromApi} = useSelector(
    (state) => state.product.options,
  );

  const countriesLoaded =
    Array.isArray(countriesFromApi) && countriesFromApi.length > 0;
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {SCORES_VIEW_TABS} = useViewTabs();

  const [content, setContent] = useState(null);
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [isMarkHovered, setIsMarkHovered] = useState(false);

  const [zoom, setZoom] = useState(language === "en" ? 8 : 4);

  const chartFontSize = (width * 0.75) / 100;
  const chartPadding = (width * 0.5) / 100;
  const chartBorder = (width * 0.25) / 100;
  const margin = (width * 0.5) / 100;

  const barLabelsList = [
    {
      id: 1,
      name:
        scores === "simulations"
          ? t("page.dashboard.simulations")
          : t("page.dashboard.average_score"),
      icon: bar_icon,
    },
  ];

  const chartData = useMemo(() => {
    let filteredRaw = Array.isArray(data?.raw)
      ? data.raw.filter(
          (item) =>
            (item.region || item.country) && (item.count || item.avg_score),
        )
      : [];

    if (filteredRaw.length > 5) {
      filteredRaw = filteredRaw
        .sort((a, b) => {
          if (scores === "simulations") {
            return (b.count || 0) - (a.count || 0);
          } else {
            return (b.avg_score || 0) - (a.avg_score || 0);
          }
        })
        .slice(0, 5);
    }

    const fullNames = filteredRaw.map(
      (item) => item.region || item.country || "",
    );
    const chartLabels = fullNames.map((rawName) => {
      const shortName =
        rawName.length > 10 ? `${rawName.slice(0, 10)}...` : rawName;
      return shortName;
    });
    const chartSimulations = filteredRaw.map((item) =>
      Math.round(item.count || 0),
    );
    const chartAvgScore = filteredRaw.map((item) =>
      item.avg_score ? Math.round(Number(item.avg_score) * 10) / 10 : 0,
    );

    while (chartLabels.length < 5) {
      chartLabels.unshift("");
      fullNames.unshift("");
      chartSimulations.unshift(0);
      chartAvgScore.unshift(0);
    }

    return {
      labels: chartLabels,
      fullNames,
      datasets: [
        {
          label:
            scores === "simulations"
              ? t("page.dashboard.simulations")
              : t("page.dashboard.average_score"),
          backgroundColor: "#8A6ACB",
          hoverBackgroundColor: "#B262C9",
          borderColor: "transparent",
          data: scores === "simulations" ? chartSimulations : chartAvgScore,
          borderRadius: margin,
          borderWidth: 2,
          barPercentage: 0.8,
        },
      ],
    };
  }, [data, scores, t, margin, language]);

  const filteredRaw = useMemo(() => {
    return Array.isArray(data?.raw)
      ? data.raw.filter(
          (item) =>
            (item.region || item.country) && (item.count || item.avg_score),
        )
      : [];
  }, [data]);

  const handleClickTab = useCallback(() => {}, []);

  const handleTabChange = useCallback(
    (value) => {
      dispatch(setScores(value));
    },
    [dispatch],
  );

  const handleMouseEnterMark = useCallback((e, item) => {
    setContent(item);
    setMouseX(e.clientX);
    setMouseY(e.clientY);
  }, []);

  const handleMouseLeaveMark = useCallback(() => {
    if (!isMarkHovered) {
      setContent(null);
      setMouseX(0);
      setMouseY(0);
    }
  }, [isMarkHovered]);

  const markers = useMemo(() => {
    if (!countriesLoaded || !filteredRaw || filteredRaw.length === 0) {
      return [];
    }

    const result = renderMarkers(
      {raw: filteredRaw},
      scores,
      handleMouseEnterMark,
      handleMouseLeaveMark,
      countriesFromApi,
    );

    if (result && result.length === 0 && filteredRaw.length > 0) {
      console.warn("Markers not found", {
        filteredRaw: filteredRaw.map((item) => ({
          country: item.country,
          region: item.region,
        })),
        countriesCount: countriesFromApi?.length,
        sampleCountries: countriesFromApi
          ?.slice(0, 3)
          .map((c) => ({name: c.name, hasGeo: !!c.geo})),
      });
    }

    return result;
  }, [
    filteredRaw,
    scores,
    handleMouseEnterMark,
    handleMouseLeaveMark,
    countriesFromApi,
    countriesLoaded,
  ]);

  const options = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 3000,
      },
      elements: {
        line: {
          borderWidth: width >= 1920 || isMobile ? 5 : chartBorder,
          capBezierPoints: false,
        },
        point: {
          radius: width >= 1920 || isMobile ? 5 : chartBorder,
          hoverRadius: width >= 1920 || isMobile ? 6 : chartBorder + 0.5,
          borderWidth: 0,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "white",
          titleColor: "#414040",
          bodyColor: "#414040",
          usePointStyles: true,
          borderColor: "rgba(247, 207, 221, 0.80)",
          padding: width >= 1920 || isMobile ? 20 : chartPadding,
          caretPadding: width >= 1920 || isMobile ? 20 : chartPadding,
          cornerRadius: width >= 1920 || isMobile ? 20 : chartPadding,
          boxPadding: width >= 1920 || isMobile ? 20 : chartPadding,
          usePointStyle: true,
          borderWidth: width >= 1920 || isMobile ? 2 : chartBorder - 1.5,
          titleAlign: "center",
          bodyAlign: "right",
          titleFont: {
            size: width >= 1920 || isMobile ? 16 : chartFontSize,
            family: "'IBM Plex Sans', sans-serif",
          },
          bodyFont: {
            size: width >= 1920 || isMobile ? 14 : chartFontSize,
            family: "'IBM Plex Sans', sans-serif",
          },
          callbacks: {
            title: (tooltipItems) => {
              if (tooltipItems.length > 0 && chartData.fullNames) {
                const [firstItem] = tooltipItems;
                const {dataIndex} = firstItem;
                const fullName = chartData.fullNames[dataIndex];
                return fullName || firstItem.label;
              }
              const [firstItem] = tooltipItems;
              return firstItem?.label || "";
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#414040",
            font: {
              size: width >= 1920 || isMobile ? 14 : chartFontSize,
              family: "'IBM Plex Sans', sans-serif",
            },
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#414040",
            font: {
              size: width >= 1920 || isMobile ? 14 : chartFontSize,
              family: "'IBM Plex Sans', sans-serif",
            },
          },
        },
      },
    }),
    [width, isMobile, chartBorder, chartPadding, chartFontSize, chartData],
  );

  const isLoading = status === "loading";
  const isError = status === "error";
  const isEmpty =
    (!data || !data.raw || data.raw.length === 0) && status === "success";
  const hasData =
    status === "success" && data && data.raw && data.raw.length > 0;

  const onSetDate = async (key, date) => {
    const isoValue = date instanceof Date ? date.toISOString() : date;
    const newDate = {...date, [key]: isoValue};
    await dispatch(setMapChartsDate(newDate));
    await dispatch(
      getSessionsByCountry({
        fromDate: newDate.from,
        toDate: newDate.to,
        withArchived,
        sessionScore: scores,
      }),
    );
  };

  const updateMap = useCallback(async () => {
    setContent(null);
    setMouseX(0);
    setMouseY(0);

    await dispatch(
      getSessionsByCountry({
        fromDate: date.from,
        toDate: date.to,
        withArchived,
        sessionScore: scores,
      }),
    );
  }, [dispatch, date.from, date.to, withArchived, scores]);

  useEffect(() => {
    const handleScroll = () => {
      setContent(null);
      setMouseX(0);
      setMouseY(0);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setContent(null);
    setMouseX(0);
    setMouseY(0);
  }, [zoom]);

  useEffect(() => {
    updateMap();
  }, [withArchived, scores, updateMap]);

  useEffect(() => {
    dispatch(getCountries());
    return () => {
      dispatch(clearOptions());
    };
  }, [dispatch]);

  useEffect(() => {
    clearCoordinatesCache();
  }, [language]);

  return (
    <div className={styles.map}>
      <div
        className={styles.mapLeft}
        style={{
          maxWidth: `100%`,
          height: `${hasData ? "auto" : "400rem"}`,
        }}
      >
        <Head
          title={<span>{t("page.dashboard.performance_title")}</span>}
          checkboxLabel={t("page.dashboard.checkbox_filter")}
          setWithArchived={setWithArchived}
          withArchived={withArchived}
          chartsDate={date}
          minDate={minDate}
          setFromDate={(date) => onSetDate("from", date)}
          setToDate={(date) => onSetDate("to", date)}
        />

        <div
          className={styles.mapImage}
          style={{
            backgroundColor: `${hasData ? "var(--greyLight)" : "transparent"}`,
            height: `${hasData ? "auto" : "100%"}`,
          }}
        >
          {isLoading && (
            <Spinner
              width={80}
              height={80}
            />
          )}

          {isError && (
            <WarningData
              type={"error"}
              text={error}
            />
          )}

          {isEmpty && <WarningData type={"empty"} />}

          {hasData && (
            <ComposableMap
              style={{height: "100%", width: "100%", cursor: "grab"}}
              projection={"geoMercator"}
              projectionConfig={{scale: 150}}
            >
              <ZoomableGroup
                onMoveStart={() => {
                  setContent(null);
                  setMouseX(0);
                  setMouseY(0);
                }}
                onMoveEnd={({zoom}) => {
                  setMouseX(0);
                  setMouseY(0);
                  setZoom(Math.max(zoom, 1));
                }}
                center={language === "en" ? [15, 45] : [60, 60]}
                zoom={zoom}
              >
                <Geographies geography={geoUrl}>
                  {({geographies}) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={"#8A6ACB"}
                        stroke={"rgba(255,255,255, 0.5)"}
                        strokeWidth={0.5}
                      />
                    ))
                  }
                </Geographies>
                {Array.isArray(markers) && markers.length > 0 ? markers : null}
              </ZoomableGroup>
            </ComposableMap>
          )}

          {content && hasData && (
            <MarkerTooltip
              enter={() => setIsMarkHovered(true)}
              leave={() => setIsMarkHovered(false)}
              item={content}
              x={mouseX}
              y={mouseY}
              language={language}
            />
          )}

          {hasData && (
            <div className={styles.mapControls}>
              <RootIcon
                disabled={zoom === (language === "en" ? 8 : 4)}
                id={"zoom-in"}
                onClickIcon={() => {
                  setZoom((prevZoom) =>
                    Math.min(prevZoom + 1, language === "en" ? 8 : 4),
                  );
                }}
              />
              <RootIcon
                disabled={zoom === 1}
                id={"zoom-out"}
                onClickIcon={() => {
                  setZoom((prevZoom) => Math.max(prevZoom - 1, 1));
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.mapRight}>
        <div className={styles.mapChartHead}>
          <H3>{t("page.dashboard.top")}</H3>

          <SwitchButton
            value={scores}
            onChange={handleTabChange}
            switchList={SCORES_VIEW_TABS}
            onClickTab={(item) => handleClickTab(item)}
            width={345}
          />
        </div>

        {!isEmpty && !isError && hasData && (
          <div className={styles.mapChartWrapper}>
            <div className={styles.mapChart}>
              <Bar
                options={options}
                data={chartData}
              />
            </div>

            <ChartLabel labelsList={barLabelsList} />
          </div>
        )}
      </div>
    </div>
  );
};
